import { useEffect } from 'react';

/**
 * A react hook to call the given handler function when the user clicks
 * outside the given element.
 *
 * Also listens on the element's own document, so it works for elements that
 * live in another window (e.g. the Document Picture-in-Picture window), where
 * events never reach the opener's `document`.
 *
 * @param ref reference to the html element
 * @param handler function to be called when the user clicks outside the element
 */
function useOnClickOutside(
  ref: React.RefObject<HTMLElement>,
  handler: () => void,
  ignoreRef?: React.RefObject<HTMLElement>,
) {
  useEffect(() => {
    const listener = (event: MouseEvent) => {
      if (
        !ref.current ||
        ref.current.contains(event.target as Node) ||
        (ignoreRef &&
          ignoreRef.current &&
          ignoreRef.current.contains(event.target as Node))
      ) {
        return;
      }
      handler();
    };

    const documents = new Set<Document>([document]);
    if (ref.current?.ownerDocument) {
      documents.add(ref.current.ownerDocument);
    }

    documents.forEach((doc) => {
      doc.addEventListener('mousedown', listener);
      doc.addEventListener(
        'touchstart',
        listener as EventListenerOrEventListenerObject,
      );
    });

    return () => {
      documents.forEach((doc) => {
        doc.removeEventListener('mousedown', listener);
        doc.removeEventListener(
          'touchstart',
          listener as EventListenerOrEventListenerObject,
        );
      });
    };
  }, [ref, handler, ignoreRef]);
}

export default useOnClickOutside;
